import { computed, defineComponent, onMounted, ref, unref, toValue } from 'vue'
import type { ISelectItemListProps, ISelectItemProps, ISelectProps, UseSelect } from './types'
import { uref } from '@/utils/reactive/uref'
import { uniqueBy, type AABB, VEC2, smoothstep, lerp } from 'math'
import {
  debouncedRef,
  notNullish,
  onClickOutside,
  useElementBounding,
  useElementSize,
  useElementVisibility
} from '@vueuse/core'
import type { IBaseComponentProps } from '../base/types'
import { useInterpolation } from '@/utils/animation/useInterpolation'
import { Grid } from 'components/grid'
import Icon from 'components/icon'
import Button from '../button'
import { FORM_INPUT_HEIGHT_PX } from '../form/constants'
import { useKeyboard } from '@/utils/hooks/useKeyboard'
import { Spinner } from '../spinner'
import { unwrapElementRef } from '@/utils/dom/unwrap'
import { twColor } from '@/utils/style'
import { getRefId } from 'findef'
import type { FormFieldOption } from '@/form/useForm'
import { useAnimationStep } from '@/utils/animation/useAnimationStep'
import ProgressBar from '../progress-bar'
import { useAsyncFetch } from '@/utils/async/useAsyncFetch'

const ITEM_HEIGHT = FORM_INPUT_HEIGHT_PX * 1.25

const getKey = (opt: FormFieldOption): any => {
  return opt.key || (opt.value ? getRefId(opt.value) : opt) || opt.value
}

const useSelect = (props: ISelectProps): UseSelect => {
  const loadingProgress = useAnimationStep({
    initial: 0,
    duration: 0.5
  })
  const isAdding = ref<boolean>(false);
  const elapsedProgress = computed(() => {
    return smoothstep(0.0, 1.0, loadingProgress.current.value);
  })

  const animation = useInterpolation({
    initial: 0.0,
    duration: 0.5
  })

  const queryString_ = ref<string | null | undefined>(null)
  const setQueryString = (value: string | null | undefined) => {
    queryString_.value = value
  }

  const queryString = debouncedRef(queryString_, 60);

  watchEffect(() => {
    console.log(`Query: ${queryString.value}`);
  })

  const isOpen = ref<boolean>(false)

  const setOpen = (open: boolean) => {
    animation.run(animation.state.value, open ? 1.0 : 0.0)
    isOpen.value = open
  }

  const toggleOpen = () => {
    setOpen(!isOpen.value)
  }

  const value = ref<any>(uref(props.value))
  const selectedKey = ref<string | null | undefined>(null)
  const sOption = ref<FormFieldOption | null | undefined>(null)

  const suggestionsQuery = useAsyncFetch<FormFieldOption[]>(
    async () => {
      if (!props.options) return []
      if (!props.options.search) return []
      if (!props.options.search.api) return []
      if (!props.options.search.api.fetchSuggestions) return []
      const items = await props.options.search.api.fetchSuggestions()
      return items
    },
    []
  )

  const optionsQuery = useAsyncFetch<FormFieldOption[]>(async () => {
    if (!props.options) return [];
    const items = props.options?.search?.api?.fetchAll
      ? await props.options.search.api.fetchAll()
      : []
    return items
  }, [])

  const searchQuery = useAsyncFetch<FormFieldOption[]>(async () => {
    if (!props.options) return []

    const q = queryString.value
    if (
      !props.options.search ||
      !props.options.search.api ||
      !props.options.search.api.fetch ||
      !q ||
      q.length <= 0
    ) {
      return props.options.items || []
    }
    const items = await props.options.search.api.fetch(q)
    //init.value = true
    return items
  }, [])

  const options = computed(() => {
    const opts = (() => {
      if (
        !queryString.value ||
        (queryString.value.length <= 0 && (suggestionsQuery.response.value||[]).length > 0)
      ) {
        return suggestionsQuery.response.value || [];
      }
      if (queryString.value && queryString.value.length > 0) {
        return searchQuery.response.value || []
      }

      if (optionsQuery.loading.value || optionsQuery.response.value.length <= 0) {
        return props.options?.items || []
      }
      return optionsQuery.response.value
    })()

    return uniqueBy([...(props.options?.items || []), ...opts], (it) => getKey(it))
  })

  //  const defaultOption = ref<FormFieldOption | null | undefined>(props.options?.defaultOption);
  const initialOption = computed(() => {
    const val =
      unref(props.value) || unref(props.dataRef?.value) || unref(props.fieldRef?.value?.value)
    if (!val) return null
    const toOption = props.options?.toOption
    if (!toOption) return null
    return toOption(val)
  })
  const selectedOption = computed(() => {
    //if (!init.value && defaultOption.value) {
    //  return defaultOption.value ? defaultOption.value || null : null
    //}
    return (
      options.value.find(
        (it) =>
          JSON.stringify(it.value) === JSON.stringify(value.value) ||
          props.value === it.value ||
          it === sOption.value ||
          (it.key && selectedKey.value && it.key === selectedKey.value)
      ) ||
      initialOption.value ||
      null
    )
  })

  const select = (option: FormFieldOption | null | undefined) => {
    sOption.value = option
    if (option) {
      value.value = option.value
      selectedKey.value = option.key
      //defaultOption.value = option.value
    } else {
      value.value = null
      selectedKey.value = null
      //defaultOption.value = null
    }

    const fieldRef = props.fieldRef

    if (fieldRef) {
      if (props.reportChange) {
        props.reportChange(
          fieldRef,
          value.value,
          typeof value.value === 'undefined' || value.value === null
        )
      }
    }

    if (props.dataRef) {
      props.dataRef.setValue(value.value)
    }
  }

  const addManually = props.options?.add?.createItem
    ? async (value: string) => {
        isAdding.value = true
        loadingProgress.to(0.25, 0.33, (start, target, mix) => lerp(start, target, smoothstep(0.0, 1.0, mix)))
        const fun = props.options?.add?.createItem as (
          value: string
        ) => Promise<FormFieldOption | undefined> | (FormFieldOption | undefined)
        const item = await fun(value)
        loadingProgress.to(0.5, 0.33)
        await searchQuery.refresh()
        loadingProgress.to(1.0, 0.15).then(() => {
          loadingProgress.to(0.0, 0.001)
          isAdding.value = false
        })
        return item
      }
    : undefined

  const loading = computed(
    () => optionsQuery.loading.value || searchQuery.loading.value || suggestionsQuery.loading.value
  )

  const locked = computed(() => !!(props.disabled || props.locked))
  return {
    key: selectedKey,
    options,
    value: value,
    selectedOption,
    select,
    isOpen,
    setOpen,
    toggleOpen,
    animation,
    queryString: queryString,
    setQueryString,
    addManually,
    locked: locked,
    loading,
    loadingProgress,
    elapsedProgress,
    isAdding
  }
}

const SelectItem = defineComponent<
  ISelectItemProps & IBaseComponentProps & { hook: UseSelect; locked?: boolean; disabled?: boolean }
>(
  (props) => {
    const elRef = ref()
    const visible = useElementVisibility(elRef)

    const isOpen = computed(() => props.transparent !== true)

    const isLocked = computed(() => props.hook.locked.value || props.locked || props.disabled)

    return () => {
      return (
        <div
          data-testid="select-item"
          class={[
            'w-full flex bg-white items-center transition-colors cursor-pointer overflow-hidden min-w-0',
            isLocked.value ? '' : 'hover:bg-gray-50',
            isOpen.value ? 'px-4' : 'px-3'
          ]}
          ref={elRef}
          style={[
            {
              ...(props.transparent
                ? {
                    background: 'transparent'
                  }
                : {
                    height: ITEM_HEIGHT + 'px'
                  })
            },
            isLocked.value
              ? {
                  pointerEvents: 'none'
                }
              : {}
          ]}
        >
          {visible.value || props.transparent
            ? (props.item.body ? <props.item.body isOpen={isOpen} /> : props.item.label) || null
            : null}
        </div>
      )
    }
  },
  { props: ['item', 'active', 'transparent', 'hook', 'locked', 'disabled'] }
)

const LoadingState = defineComponent(() => {
  return () => {
    return (
      <div class="rounded-sm bg-white overflow-hidden flex flex-col px-4 py-4 gap-4">
        <div class="flex-none flex items-center">
          <span class="text-gray-500 text-sm font-medium font-['Inter']">Matching Results:</span>
        </div>
        <div class="text-gray-900 text-sm font-medium font-['Inter'] leading-[17.50px] flex flex-row items-center gap-2">
          <Spinner class="w-5 h-5" />
          <span>Searching ...</span>
        </div>
      </div>
    )
  }
})

const EmptyListState = defineComponent(() => {
  return () => {
    return (
      <div class="rounded-sm bg-white overflow-hidden flex flex-col px-4 py-4 gap-4">
        <div class="flex-none flex items-center">
          <span class="text-gray-500 text-sm font-medium font-['Inter']">Matching Results</span>
        </div>
        <div class="text-gray-900 text-sm font-medium font-['Inter'] leading-[17.50px] flex flex-row items-center gap-2">
          <Icon
            src={{
              src: async () => import('assets/img/svg/icons/search.svg?raw'),
              stroke: 'currentColor',
              size: '0.875rem'
            }}
          />
          <span>Nothing found ...</span>
        </div>
      </div>
    )
  }
})

const SelectItemList = defineComponent<ISelectItemListProps>(
  (props) => {
    const myRef = ref()
    //const mySize = useElementSize(myRef)
    const anchorSize = useElementSize(props.anchor)
    const bounding = useElementBounding(props.anchor)
    const bounds = computed((): AABB => {
      const pos = VEC2(bounding.x.value, bounding.y.value)
      const size = VEC2(bounding.width.value, bounding.height.value)
      return {
        min: pos,
        max: pos.add(size)
      }
    })

    onMounted(() => {
      setTimeout(() => {
        bounding.update()
      }, 1000)
    })
    const maxHeight = computed(() => {
      return ITEM_HEIGHT * 4
    })

    const targetPos = computed(() => {
      //  const myW = mySize.width.value
      // const myH = mySize.height.value
      // const anchorW = anchorSize.width.value
      // const anchorH = anchorSize.height.value
      const anchorBounds = bounds.value

      const x = anchorBounds.min.x
      const y = anchorBounds.min.y + FORM_INPUT_HEIGHT_PX + 12

      return VEC2(x, y)
    })

    const opacity = computed(() => {
      const f = props.hook.animation.state.value
      const sf = smoothstep(0.0, 1.0, f)
      return sf
    })

    const handleClick = (event: Event, item: FormFieldOption) => {
      event.stopImmediatePropagation()
      event.stopPropagation()
      event.preventDefault()
      props.hook.select(item)
      props.hook.setOpen(false)
    }

    const handleManuallyAdd = async () => {
      if (!props.hook.addManually) return
      props.hook.setOpen(false)
      const item = await props.hook.addManually(props.hook.queryString.value || '')
      props.hook.select(item)
    }

    const manuallyAddIsVisible = computed(
      () =>
        props.hook.queryString.value &&
        props.hook.queryString.value.length &&
        !!props.hook.addManually
    )

    return () => {
      return (
        <div
          class={
            'rounded-sm bg-white shadow-sm overflow-hidden flex flex-col' +
            (manuallyAddIsVisible.value ? '' : ' pb-1')
          }
          ref={myRef}
          style={{
            position: 'fixed',
            left: targetPos.value.x + 'px',
            top: targetPos.value.y + 'px',
            width: anchorSize.width.value + 'px',
            overflow: 'hidden',
            zIndex: 2,
            opacity: `${opacity.value * 100}%`,
            ...(opacity.value <= 0.1
              ? {
                  pointerEvents: 'none'
                }
              : {})
          }}
        >
          {props.warning && props.hook.queryString.value && props.hook.queryString.value.length > 0 && (
            <div class="px-4 py-2.5 bg-gray-100 border-b border-gray-200 flex items-center gap-2">
              <Icon
                src={{
                  src: async () => import('assets/img/svg/icons/warning_hex.svg?raw'),
                  fill: '#6b7280',
                  size: '0.875rem'
                }}
              />
              <span class="text-gray-500 text-xs font-medium font-['Inter']">{props.warning}</span>
            </div>
          )}
          {props.hook.options.value.length > 0 && !props.hook.loading.value && (
            <div class="flex-none px-4 py-4 flex items-center">
              <span class="text-gray-500 text-sm font-medium font-['Inter']">
                {props.hook.queryString.value && props.hook.queryString.value.length > 0
                  ? 'Matching Results:'
                  : 'Suggestions:'}
              </span>
            </div>
          )}
          {props.hook.options.value.length > 0 && !props.hook.loading.value ? (
            <div
              class="pretty-scrollbar-opaque flex-1"
              ref={myRef}
              style={{
                maxHeight: maxHeight.value + 8 + 2 + 'px',
                overflow: 'auto'
              }}
            >
              {props.hook.options.value.map((item) => {
                const key =
                  item.key ||
                  [item.label, item.value, item.name]
                    .filter(notNullish)
                    .map((it) => it + '')
                    .join('-')
                return (
                  <SelectItem
                    data-cy="select-item"
                    key={key}
                    onClick={(event) => handleClick(event, item)}
                    item={item}
                    active={item.value === props.hook.selectedOption.value}
                    hook={props.hook}
                    locked={props.locked || props.disabled}
                    disabled={props.locked || props.disabled}
                  />
                )
              })}
            </div>
          ) : (
            <div class="bg-white w-full">
              {props.hook.loading.value ? <LoadingState /> : <EmptyListState />}
            </div>
          )}
          {manuallyAddIsVisible.value && (
            <div
              onClick={handleManuallyAdd}
              class="group w-full h-16 px-4 flex items-center cursor-pointer"
            >
              <div class="h-full flex items-center w-full border-t border-gray-100">
                <Grid
                  columns="max-content max-content"
                  alignItems="center"
                  gap="1rem"
                  class="transition-opacity group-hover:opacity-80"
                >
                  <Button
                    style={{ height: '1.25rem', width: '1.25rem', aspectRatio: '1 / 1' }}
                    variant="secondary"
                    size="s"
                    onlyIcon
                    icon={{
                      src: async () => import('assets/img/svg/icons/plus.svg?raw'),
                      fill: twColor('primary-500'),
                      size: '0.75rem'
                    }}
                  />
                  <span class="text-primary-500 text-sm font-semibold font-['Inter']">
                    Manually add "{props.hook.queryString.value}"
                  </span>
                </Grid>
              </div>
            </div>
          )}
        </div>
      )
    }
  },
  { props: ['anchor', 'hook', 'locked', 'disabled', 'warning'] }
)

export const Select = defineComponent<ISelectProps>(
  (props) => {
    const hook = useSelect(props)
    const anchor = ref()
    const itemListRef = ref()
    const warningText = computed(() => toValue(props.options?.warning))

    const inputRef = ref()

    const handleInput = (event: Event) => {
      if (props.locked || props.disabled) return
      if (props.dataRef) {
        props.dataRef.setTouched(true)
      }

      event.stopImmediatePropagation()
      event.stopPropagation()
      event.preventDefault()

      if (!hook.isOpen.value) {
        hook.setOpen(true)
      }

      const target = event.target as HTMLInputElement
      hook.setQueryString(target.value)
    }

    onClickOutside(
      itemListRef,
      () => {
        if (!hook.isOpen.value) return
        hook.setOpen(false)

        if (!hook.value.value) {
          const fieldRef = props.fieldRef
          if (fieldRef && props.reportChange) {
            props.reportChange(fieldRef, null, true)
          }
          if (fieldRef && props.triggerBlur) {
            props.triggerBlur(fieldRef)
          }
        }
      },
      { ignore: [anchor] }
    )

    const keyboard = useKeyboard()
    keyboard.addEventListener('keyup', async (ev) => {
      switch (ev.key) {
        case 'Escape':
          {
            hook.setOpen(false)
          }
          break
        case 'Enter':
          {
            const inputValue = hook.queryString.value
            if (
              props.options?.add?.invokeOnPressEnter &&
              hook.isOpen.value &&
              inputValue &&
              inputValue.length > 0 &&
              hook.addManually
            ) {
              const item = await hook.addManually(inputValue)
              hook.select(item)
              hook.setOpen(false)
            }
          }
          break
      }
    })

    const handleUnset = () => {
      if (props.locked || props.disabled) return
      hook.select(null)

      queueMicrotask(() => {
        const inputEl = unwrapElementRef(inputRef) as HTMLInputElement
        if (inputEl) {
          inputEl.focus()
          queueMicrotask(() => {
            hook.setOpen(true)
          })
        }
      })
    }

    return () => {
      return (
        <div>
          <div class="w-full relative select-none">
            <div
              ref={anchor}
              class="w-full border border-gray-300 relative"
              style={{
                height: FORM_INPUT_HEIGHT_PX + 'px',
                overflow: 'hidden'
              }}
            >
              {hook.selectedOption.value ? (
                <Grid
                  onClick={handleUnset}
                  noDefaultLayout
                  class={[
                    'w-full h-full transition-all overflow-hidden',
                    props.locked || props.disabled
                      ? 'cursor-not-allowed opacity-60'
                      : 'hover:bg-gray-100'
                  ]}
                  columns="1fr max-content"
                  style={{ pointerEvents: 'all' }}
                >
                  <SelectItem
                    transparent
                    item={hook.selectedOption.value}
                    hook={hook}
                    disabled={props.locked || props.disabled}
                    locked={props.locked || props.disabled}
                  />
                </Grid>
              ) : (
                <Grid
                  onClick={() => hook.setOpen(true)}
                  noDefaultLayout
                  class="w-full h-full"
                  columns={props.icon ? 'max-content 1fr' : '1fr'}
                  alignItems="center"
                >
                  {props.icon && (
                    <Grid
                      noDefaultLayout
                      alignItems="center"
                      class="text-gray-500 h-full px-2"
                      justifyContent="center"
                    >
                      <Icon src={props.icon} />
                    </Grid>
                  )}
                  <input
                    data-cy="select-input"
                    ref={inputRef}
                    onInput={handleInput}
                    onFocus={props.onFocus}
                    onBlur={props.onBlur}
                    value={hook.queryString.value || ''}
                    class="w-full h-full border-none focus:ring-0 ring-0 placeholder:text-sm text-sm text-gray-900"
                    type="text"
                    placeholder={props.placeholder || ''}
                    style={{
                      margin: '0px',
                      outline: 'none',
                      outlineStyle: 'none',
                      outlineColor: 'transparent',
                      background: 'transparent',
                      ...(props.icon
                        ? {
                            padding: '0px'
                          }
                        : {})
                    }}
                  />
                </Grid>
              )}
            </div>
            <SelectItemList
              ref={itemListRef}
              hook={hook}
              anchor={anchor}
              locked={props.locked}
              disabled={props.disabled}
              warning={warningText.value}
            />
          </div>
          <ProgressBar size="xs" scale100 square value={hook.elapsedProgress.value}/>
        </div>
      )
    }
  },
  {
    props: [
      'value',
      'options',
      'fieldRef',
      'dataRef',
      'reportChange',
      'icon',
      'placeholder',
      'triggerBlur',
      'onFocus',
      'onBlur',
      'locked',
      'disabled'
    ]
  }
)
